import React from 'react';
import { BookOpen, ExternalLink } from 'lucide-react';
import CollapsiblePanel from './CollapsiblePanel';
import ExpandableDescription from './ExpandableDescription';
import { buildDocumentSharePath } from '../utils/documentLinks';

export default function PortalNotificationsPanel({
  error,
  expanded,
  hasLoaded,
  isLoading,
  items,
  onToggle,
  extraHeaderAction
}) {
  if (!isLoading && !hasLoaded) {
    return null;
  }

  return (
    <CollapsiblePanel
      extraHeaderAction={extraHeaderAction}
      collapsedSummary={(
        <div className="text-[10px] leading-snug font-bold text-left text-gray-500 dark:text-gray-400">
          Últimas publicações
        </div>
      )}
      countBadge={!isLoading ? (
        <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-sky-100 dark:bg-sky-900/30 text-[10px] font-black text-sky-700 dark:text-sky-300">
          {items.length}
        </span>
      ) : null}
      expanded={expanded}
      icon={BookOpen}
      iconClassName="bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400"
      onToggle={onToggle}
      title="Últimos 15 dias"
      wrapperClassName={`flex flex-col transition-all duration-300 ${expanded ? 'flex-1 min-h-0 lg:overflow-hidden' : 'shrink-0'}`}
    >
      <div className="px-3 pb-3 flex flex-col min-h-0 flex-1 lg:overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 flex-1 overflow-hidden pr-1">
            {[1, 2, 3].map((index) => (
              <div key={index} className="block rounded-xl border border-sky-100/50 dark:border-sky-900/20 bg-sky-50/50 dark:bg-sky-900/10 p-2.5 animate-pulse">
                <div className="h-2.5 bg-sky-200/50 dark:bg-sky-800/50 rounded w-16 mb-2"></div>
                <div className="h-3 bg-sky-200/50 dark:bg-sky-800/50 rounded w-full mb-1"></div>
                <div className="h-3 bg-sky-200/50 dark:bg-sky-800/50 rounded w-3/4 mb-3"></div>
                <div className="flex justify-between">
                  <div className="h-2 bg-sky-200/50 dark:bg-sky-800/50 rounded w-12"></div>
                  <div className="h-2 bg-sky-200/50 dark:bg-sky-800/50 rounded w-16"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 px-3 py-3 text-xs text-red-700 dark:text-red-200">
            {error}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700 px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
            Não foram identificados novos documentos publicados nos últimos 15 dias.
          </div>
        ) : (
          <div className="space-y-2 flex-1 min-h-0 overflow-y-auto pr-1">
            {items.map((item) => (
              <a
                key={`${item.url}-${item.publishedAt || item.data}`}
                href={buildDocumentSharePath(item)}
                className="block rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 p-2.5 hover:border-sky-200 dark:hover:border-sky-700/40 hover:bg-sky-50/70 dark:hover:bg-sky-900/10 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 dark:text-sky-400">
                      {item.tipo || 'Arquivo'}
                    </p>
                    <p className="mt-0.5 text-xs font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
                      {item.nome}
                    </p>
                    {item.description && (
                      <ExpandableDescription
                        text={item.description}
                        className="mt-1 text-[10px] text-gray-500 dark:text-gray-400"
                        buttonClassName="text-sky-600 dark:text-sky-400"
                      />
                    )}
                  </div>
                  <ExternalLink className="h-3 w-3 text-sky-500 shrink-0 mt-0.5" />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                  <span className="break-words sm:truncate sm:max-w-[120px]" title={item.portal}>{item.portal}</span>
                  <div className="flex flex-col items-end">
                    {(() => {
                      if (!item.data) return null;
                      try {
                        const [day, month, year] = item.data.split('/').map(Number);
                        const docDate = new Date(year, month - 1, day);
                        if (isNaN(docDate.getTime())) return null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        docDate.setHours(0, 0, 0, 0);
                        const diffDays = Math.floor((today - docDate) / (1000 * 60 * 60 * 24));
                        if (diffDays >= 0 && diffDays <= 5) {
                          return (
                            <span className="mb-0.5 px-1.5 py-0.5 bg-emerald-500 text-white text-[7px] font-black rounded-sm uppercase tracking-tighter leading-none shadow-sm">
                              NOVO
                            </span>
                          );
                        }
                      } catch (e) { return null; }
                      return null;
                    })()}
                    <span className="font-semibold text-sky-700 dark:text-sky-300">{item.data || 'Sem data'}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </CollapsiblePanel>
  );
}
